/* The packs switched on here, asked once per page and shared.
 *
 * Every work screen needs them (words, lists, kinds, who may create), and
 * asking on each screen made the home, the form and the item each wait for
 * the same answer.
 */
import { useCallback, useEffect, useState } from 'react'
import type { ErpClient } from '../api'
import type { ErpError } from '../api'
import { useErp } from '../context'
import type { PacksActive } from './types'

const cache = new WeakMap<ErpClient, Promise<PacksActive>>()

export function forgetPacks(erp: ErpClient) { cache.delete(erp) }

export function usePacksActive(): {
  active: PacksActive | null; failed: ErpError | null; reload: () => void
} {
  const erp = useErp()
  const [active, setActive] = useState<PacksActive | null>(null)
  const [failed, setFailed] = useState<ErpError | null>(null)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    let live = true
    let pending = cache.get(erp)
    if (!pending) {
      pending = erp.packsActive()
      cache.set(erp, pending)
      // A failure is not remembered: the next screen asks again.
      pending.catch(() => cache.delete(erp))
    }
    pending.then((a) => { if (live) { setActive(a); setFailed(null) } })
           .catch((e) => { if (live) setFailed(e as ErpError) })
    return () => { live = false }
  }, [erp, tick])

  const reload = useCallback(() => { cache.delete(erp); setTick((t) => t + 1) }, [erp])
  return { active, failed, reload }
}
